import { EventEmitter, Injectable, inject } from '@angular/core';
import { FilterPreferenceLocksService } from '@clubhub/ignite/services/filter-preferences/filter-preference-locks.service';
import { BridgingProduct, LendingTypeCode, MatchedLenderProducts, MiProductsFiltersViewModel, Product } from '@shared/models';
import { LendingTypeService } from '@shared/services/lending-type.service';
import {
  FilterOptionsContext,
  ProductFilterDefinition,
  ProductFilterGroupDefinition,
  ProductsFilterContext,
  ProductsFilterHelper,
} from '../products/products-filter/products-filter.helper';

export interface ProductFilter {
  title: string;
  checked: boolean;
  /**
   * The broker's firm has marked this filter "can't be overridden", so it is shown but not editable.
   *
   * Presentation only. The resolved preferences already carry the firm's value for a locked slot, and the API
   * re-applies every lock on save - a client is never trusted with the enforcement.
   */
  locked?: boolean;
  included: boolean;
  excluded: boolean;
  onActivateMessage?: string | null;
  subFilters?: {
    items: ProductFilterDefinition<Product | BridgingProduct>[];
    checkedItems: Set<string>;
  };
  hasSubFilters?: boolean;
  hasNumericValue?: boolean;
  numericValue?: number;
  numericFilterDescription?: string;
  numericFilterPrefix?: string;
  hasRangeValues?: boolean;
  rangeFilterUnits?: string;
  fromValue?: number;
  toValue?: number;
  validationErrors?: string;
}

export type ProductFilterGroup = {
  title: string;
  /** The broker's firm has locked this whole group, so every option in it is read-only. */
  locked?: boolean;
  items: readonly ProductFilter[];
  disabled: boolean;
  isIncludeExcludeFilter?: boolean;
  groupInfoText?: string;
  beforeText?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ProductsFilterService {
  private readonly filterPreferenceLocks = inject(FilterPreferenceLocksService);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly productsFilterHelper = inject(ProductsFilterHelper);

  public filterChanged = new EventEmitter<boolean>();
  private checkedFilters: Map<string, Set<string>> | undefined;
  private readonly includeExcludeGroupsFilters: Map<string, any> = new Map<string, unknown>();
  private readonly numericValueFilters: Map<string, number | undefined> = new Map<string, number | undefined>();
  private readonly rangeValueFilters: Map<string, [number | undefined, number | undefined]> = new Map<
    string,
    [number | undefined, number | undefined]
  >();
  private readonly availableFiltersCache = new Map<string, boolean | undefined>();
  private isPristine = true;

  private productFilterDefinitions: ProductFilterGroupDefinition<Product | BridgingProduct>[] = [];
  private isBridgingType: boolean = false;
  public productsFilterContext: ProductsFilterContext | undefined;

  public constructor() {
    this.setProductFilterDefinitions();
  }

  /**
   * Rebuilds the filter definitions for the current lending type and search context.
   *
   * Runs on every search, because which filters are offered depends on the request. It used to recreate the
   * checked selections empty while it was at it, so editing a search or adding another tool and searching again
   * wiped every ticked sidebar filter. Selections now carry across the rebuild; clearing them is left to
   * `clearFilters`, which is what New search goes through.
   */
  public setProductFilterDefinitions(): void {
    const lendingTypeCode = this.lendingTypeService.lendingType?.code?.replaceAll(' ', '').toLowerCase();
    this.isBridgingType = lendingTypeCode === LendingTypeCode.Bdg.toLowerCase();

    if (this.isBridgingType) {
      this.productFilterDefinitions = this.productsFilterHelper.bridgingProductFilterDefinitions as any;
    } else {
      this.productFilterDefinitions = this.productsFilterHelper.resAndBtlProductFilterDefinitions(
        lendingTypeCode === LendingTypeCode.Btl.toLowerCase(),
      ) as any;
    }

    const previous = this.checkedFilters;
    this.checkedFilters = new Map(
      this.productFilterDefinitions.map(g => [g.title, new Set(previous?.get(g.title) ?? [])]),
    );
  }

  private getIncludeExcludeFilterContexts() {
    if (this.includeExcludeGroupsFilters?.size > 0) {
      return [...this.includeExcludeGroupsFilters.keys()].map(key => {
        const group = this.productFilterDefinitions.find(i => i.title === key);
        const includeExcludeGroupFilters = this.includeExcludeGroupsFilters.get(key);
        return {
          group,
          includedItems: [...includeExcludeGroupFilters.includedFilterDefs.keys()],
          excludedItems: [...includeExcludeGroupFilters.excludedFilterDefs.keys()],
        };
      });
    }

    return [];
  }

  /** Gets all 'active' filters, including those that have been overwritten to be active */
  private getActiveFilters(ctx: FilterOptionsContext<Product | BridgingProduct>, includeOverridden: boolean) {
    return this.productFilterDefinitions.map(group => {
      const checkedGroupFilters = this.checkedFilters?.get(group.title);
      const legacyItems = checkedGroupFilters
        ? [...checkedGroupFilters]
        : [];
      return {
        group,
        items:
          'items' in group
            ? group.items
              .filter(i => (includeOverridden ? i.overrideChecked?.(ctx) : null) ?? this.checkedFilters?.get(group.title)?.has(i.title))
              .map(i => i.title)
            : legacyItems,
      };
    });
  }

  public hasActiveIncludeFilter(group: string, name: string): boolean {
    return this.includeExcludeGroupsFilters.get(group)?.includedFilterDefs?.has(name);
  }

  /** Gets an object for the backend to replicate the selected filters. */
  public getSelectedFilterNames(context: FilterOptionsContext<Product | BridgingProduct>): Record<string, string[]> {
    context ??= {} as any;
    const obj = {};
    this.getActiveFilters(context, true).forEach(({ group, items }) => {
      (obj as any)[group.requestObjKey] = [...((obj as any)[group.requestObjKey] ?? []), ...items];
    });

    const appliedNumericValueFilters = this.getAllNumericValueFilterKeys();
    appliedNumericValueFilters.forEach(filterTitle => {
      const group = this.getGroupByFilterTitle(filterTitle);
      if (group && (obj as any)[group.requestObjKey]) {
        (obj as any)[group.requestObjKey].push(filterTitle);
      }
    });

    const appliedRangeFilters = this.getAllRangeFilterKeys();
    appliedRangeFilters.forEach(filterTitle => {
      const group = this.getGroupByFilterTitle(filterTitle);
      if (group && (obj as any)[group.requestObjKey]) {
        (obj as any)[group.requestObjKey].push(filterTitle);
      }
    });

    const filterOptions = this.getFilterOptions(context);
    const includeExcludeGroupsFilter = this.getIncludeExcludeFilterContexts();
    includeExcludeGroupsFilter.forEach(({ group, includedItems, excludedItems }) => {
      if (!group) {
        return;
      }

      const includedFieldName = `${group.requestObjKey}Included`;
      const excludedFieldName = `${group.requestObjKey}Excluded`;

      const groupItems = filterOptions.find(g => g.title === group.title)?.items ?? [];
      const getIncludedExcludedFilters = (items: any) => {
        return items
          .map((itemTitle: any) => {
            const filterWithSubFilters = groupItems.find(i => i.title === itemTitle);
            if (!filterWithSubFilters) {
              return null;
            }

            if (filterWithSubFilters.hasSubFilters) {
              return {
                title: itemTitle,
                hasSubFilters: true,
                checkedSubItems: [...(filterWithSubFilters.subFilters?.checkedItems ?? [])],
              };
            }

            return {
              title: itemTitle,
              hasSubFilters: false,
            };
          })
          .filter((f: any) => !!f);
      };

      const includedFilters = getIncludedExcludedFilters(includedItems);
      const excludedFilters = getIncludedExcludedFilters(excludedItems);

      (obj as any)[includedFieldName] = [...((obj as any)[includedFieldName] ?? []), ...includedFilters];
      (obj as any)[excludedFieldName] = [...((obj as any)[excludedFieldName] ?? []), ...excludedFilters];
    });

    const rangeFilters: { name: string; from: number | undefined; to: number | undefined }[] = [];
    for (const item of [...this.rangeValueFilters]) {
      const [key, value] = item;
      rangeFilters.push({
        name: key,
        from: value[0],
        to: value[1],
      });
    }
    (obj as any)['rangeFilters'] = rangeFilters;

    return obj;
  }

  public getFilterOptions(ctx: FilterOptionsContext<Product | BridgingProduct>): ProductFilterGroup[] {
    ctx ??= {} as any;
    return this.productFilterDefinitions.map(group => {
      const isIncludeExcludeFilter = group.isIncludeExcludeFilter;
      const groupFilters = isIncludeExcludeFilter ? this.includeExcludeGroupsFilters.get(group.title) : null;

      return {
        title: group.title,
        isIncludeExcludeFilter,
        hasSubFilters: group.hasSubFilters,
        groupInfoText: group.groupInfoText,
        beforeText: group.beforeText,
        items: ('itemFactory' in group ? group.itemFactory(ctx) : group.items)
          .filter(item => (item.visible?.(ctx) ?? true) && this.isFilterAvailable(group, item, ctx))
          .map(filter => {
            const includedFilterDef = groupFilters?.includedFilterDefs?.get(filter.title);
            const excludedFilterDef = groupFilters?.excludedFilterDefs?.get(filter.title);

            return {
              title: filter.title,
              locked: this.filterPreferenceLocks.isLocked('product', group.title),
              checked: filter.overrideChecked?.(ctx) ?? this.checkedFilters?.get(group.title)?.has(filter.title),
              included: !!includedFilterDef,
              excluded: !!excludedFilterDef,
              onActivateMessage: filter.onActivateMessage,
              hasSubFilters: filter.hasSubFilters,
              numericFilterDescription: filter.numericFilterDescription,
              numericFilterPrefix: filter.numericFilterPrefix,
              hasNumericValue: filter.hasNumericValue,
              hasRangeValues: filter.isRangeFilter,
              rangeFilterUnits: filter.rangeFilterUnits,
              fromValue: filter.isRangeFilter ? this.getRangeFilterValue(filter.title)?.[0] : undefined,
              toValue: filter.isRangeFilter ? this.getRangeFilterValue(filter.title)?.[1] : undefined,
              numericValue: filter.hasNumericValue ? this.getNumericFilterValue(filter.title) : undefined,
              subFilters: {
                items: filter.hasSubFilters && filter.itemFactory ? filter.itemFactory(ctx) : [],
                checkedItems: (includedFilterDef || excludedFilterDef)?.checkedSubFilters || new Set<string>(),
              },
            } as ProductFilter;
          }),
        locked: this.filterPreferenceLocks.isLocked('product', group.title),
        disabled: group.disabled?.(ctx) ?? false,
      } as ProductFilterGroup;
    });
  }

  public get currentAppliedFilters(): MiProductsFiltersViewModel[] {
    const checkedFilters = [...(this.checkedFilters ?? [])]
      .flatMap(filterGroup => {
        // filterGroup -> [ "filterGroupName", <set of applied filters>]
        const checkedFilters = [...filterGroup[1].values()];
        return checkedFilters.map(
          checkedFilter =>
            ({
              filterGroup: filterGroup[0],
              filterName: checkedFilter,
              isChecked: true,
              isIncluded: false,
              isExcluded: false,
              checkedSubFilters: [],
            }) as MiProductsFiltersViewModel,
        );
      });

    const includedExcludedFilters = [...this.includeExcludeGroupsFilters]
      .flatMap(groupKeyValue => {
        const groupName = groupKeyValue[0];
        const groupFilters = groupKeyValue[1];

        const excludedFilters = [...groupFilters.excludedFilterDefs].map(excFilterDef => {
          const excludeFilterName = excFilterDef[0];
          const excludeFilterValue = excFilterDef[1];
          let excludeCheckedSubFilters: string[] = [];
          if (excludeFilterValue.checkedSubFilters?.size) {
            excludeCheckedSubFilters = [...excludeFilterValue.checkedSubFilters.values()];
          }

          return {
            filterGroup: groupName,
            filterName: excludeFilterName,
            isExcluded: true,
            isIncluded: false,
            isChecked: false,
            checkedSubFilters: excludeCheckedSubFilters,
          } as MiProductsFiltersViewModel;
        });

        const includedFilters = [...groupFilters.includedFilterDefs].map(includeFilterKeyValue => {
          const incFilterName = includeFilterKeyValue[0];
          const incFilterValue = includeFilterKeyValue[1];

          let checkedSubFilters: string[] = [];
          if (incFilterValue.checkedSubFilters?.size) {
            checkedSubFilters = [...incFilterValue.checkedSubFilters.values()];
          }

          return {
            filterGroup: groupName,
            filterName: incFilterName,
            isIncluded: true,
            isExcluded: false,
            isChecked: false,
            checkedSubFilters,
          } as MiProductsFiltersViewModel;
        });

        return includedFilters.concat(excludedFilters);
      });

    const numericValueFilters = this.getAllNumericValueFilterKeys().map(filterKey => {
      return {
        filterGroup: this.getGroupTitleByFilterTitle(filterKey),
        filterName: filterKey,
        isIncluded: false,
        isExcluded: false,
        isChecked: true,
        checkedSubFilters: [],
        hasNumericValue: true,
        numericValue: this.numericValueFilters.get(filterKey),
      } as MiProductsFiltersViewModel;
    });

    const rangeFilters = this.getAllRangeFilterKeys().map(filterKey => {
      return {
        filterGroup: this.getGroupTitleByFilterTitle(filterKey),
        filterName: filterKey,
        isIncluded: false,
        isExcluded: false,
        isChecked: true,
        checkedSubFilters: [],
        hasNumericValue: false,
        numericValue: 0,
        fromValue: this.rangeValueFilters.get(filterKey)?.[0],
        toValue: this.rangeValueFilters.get(filterKey)?.[1],
        hasRangeValue: true,
      } as MiProductsFiltersViewModel;
    });

    return checkedFilters.concat(includedExcludedFilters).concat(numericValueFilters).concat(rangeFilters);
  }

  public applySavedFilters(savedFilters: MiProductsFiltersViewModel[]) {
    if (!savedFilters?.length) {
      return;
    }

    this.clearFilters();

    const excludeIncludeFilters = savedFilters.filter(f => f.isIncluded || f.isExcluded);
    for (const filter of excludeIncludeFilters) {
      this.setIncludeExcludeFilter(
        filter.filterGroup,
        filter.filterName,
        filter.isIncluded,
        filter.isExcluded,
        true,
        filter.checkedSubFilters,
      );
    }

    const checkedFilters = savedFilters.filter(f => f.isChecked);
    for (const filter of checkedFilters) {
      if (filter.hasNumericValue) {
        if (filter.numericValue) {
          this.setNumericValueFilter(
            {
              title: filter.filterName,
              checked: filter.isChecked,
              hasNumericValue: filter.hasNumericValue,
              numericValue: filter.numericValue,
            } as ProductFilter,
            filter.isChecked,
            true,
          );
        }
      } else if (filter.hasRangeValue) {
        if (filter.fromValue || filter.toValue) {
          this.setRangeValueFilter(
            {
              title: filter.filterName,
              checked: filter.isChecked,
              hasRangeValues: filter.hasRangeValue,
              fromValue: filter.fromValue,
              toValue: filter.toValue,
            } as ProductFilter,
            filter.isChecked,
            true,
          );
        }
      } else {
        this.setFilter(filter.filterGroup, filter.filterName, filter.isChecked, true);
      }
    }
    this.filterChanged.emit();
  }

  public setIncludeExcludeFilter(
    groupName: string,
    filterName: string,
    includeChecked: boolean,
    excludeChecked: boolean,
    bulkUpdate = false,
    subFilters: string[] = [],
  ) {
    const groupFilterDef = this.productFilterDefinitions.find(p => p.title === groupName);

    const itemFiltersDefs: ProductFilterDefinition<Product | BridgingProduct>[] | undefined = (groupFilterDef as any)?.['items'];
    const filterDef = itemFiltersDefs?.find(i => i.title === filterName);

    // If filter could not be found (e.g. it has been removed), do nothing
    if (!filterDef) {
      return;
    }

    if (!this.includeExcludeGroupsFilters.has(groupName)) {
      this.includeExcludeGroupsFilters.set(groupName, {
        includedFilterDefs: new Map<string, ProductFilterDefinition<Product | BridgingProduct>>(),
        excludedFilterDefs: new Map<string, ProductFilterDefinition<Product | BridgingProduct>>(),
      });
    }

    this.isPristine = false;

    const groupFilters = this.includeExcludeGroupsFilters.get(groupName);
    if (!includeChecked && !excludeChecked) {
      if (groupFilters.includedFilterDefs.has(filterName)) {
        groupFilters.includedFilterDefs.delete(filterName);
      }

      if (groupFilters.excludedFilterDefs.has(filterName)) {
        groupFilters.excludedFilterDefs.delete(filterName);
      }

      if (!bulkUpdate) {
        this.filterChanged.emit();
      }
      return;
    }

    if (!filterDef.isMatchFunc && !filterDef.hasSubFilters) {
      if (!bulkUpdate) {
        this.filterChanged.emit();
      }
      return;
    }

    if (includeChecked) {
      if (groupFilters.excludedFilterDefs.has(filterName)) {
        groupFilters.excludedFilterDefs.delete(filterName);
      }
      if (!groupFilters.includedFilterDefs.has(filterName)) {
        groupFilters.includedFilterDefs.set(filterName, {
          title: filterDef.title,
          isMatchFunc: filterDef.isMatchFunc,
          isUnknownFunc: filterDef.isUnknownFunc,
          hasSubFilters: filterDef.hasSubFilters,
          itemFactory: filterDef.itemFactory,
          isMatchFuncFactory: filterDef.isMatchFuncFactory,
          checkedSubFilters: new Set<string>(subFilters ?? []),
        });
      }
    } else {
      if (groupFilters.includedFilterDefs.has(filterName)) {
        groupFilters.includedFilterDefs.delete(filterName);
      }
      if (!groupFilters.excludedFilterDefs.has(filterName)) {
        groupFilters.excludedFilterDefs.set(filterName, {
          title: filterDef.title,
          isMatchFunc: filterDef.isMatchFunc,
          isUnknownFunc: filterDef.isUnknownFunc,
          isExcludeFunc: filterDef.isExcludeFunc,
          hasSubFilters: filterDef.hasSubFilters,
          itemFactory: filterDef.itemFactory,
          isMatchFuncFactory: filterDef.isMatchFuncFactory,
          checkedSubFilters: new Set<string>(),
        });
      }
    }

    if (!bulkUpdate) {
      this.filterChanged.emit();
    }
  }

  public isNumericFilterChecked(productTitle: string): boolean {
    return this.numericValueFilters.has(productTitle);
  }

  public isRangeFilterChecked(productTitle: string): boolean {
    return this.rangeValueFilters.has(productTitle);
  }

  public getNumericFilterValue(productTitle: string): number | undefined {
    if (this.isNumericFilterChecked(productTitle)) {
      return this.numericValueFilters.get(productTitle);
    }
  }

  public getRangeFilterValue(productTitle: string) {
    if (this.isRangeFilterChecked(productTitle)) {
      return this.rangeValueFilters.get(productTitle);
    }
  }

  public setNumericValueFilter(productFilter: ProductFilter, isChecked: boolean, bulkUpdate = false) {
    const hasFilter = this.numericValueFilters.has(productFilter.title);
    if (hasFilter) {
      this.numericValueFilters.delete(productFilter.title);
    }

    if (isChecked) {
      this.numericValueFilters.set(productFilter.title, productFilter.numericValue);
    }

    if (!bulkUpdate) {
      this.filterChanged.emit();
    }
  }

  public setRangeValueFilter(productFilter: ProductFilter, isChecked: boolean, bulkUpdate = false) {
    const hasFilter = this.rangeValueFilters.has(productFilter.title);
    if (hasFilter) {
      this.rangeValueFilters.delete(productFilter.title);
    }

    if (isChecked) {
      this.rangeValueFilters.set(productFilter.title, [productFilter.fromValue, productFilter.toValue]);
    }

    if (!bulkUpdate) {
      this.filterChanged.emit();
    }
  }

  public setRangeFilters(title: string, fromValue: number, toValue: number) {
    this.rangeValueFilters.set(title, [fromValue, toValue]);
    this.filterChanged.emit();
  }

  public getFilterDefinitionByTitle(filterTitle: string) {
    return this.productFilterDefinitions
      .map((g: any) => {
        const items = g['items'];
        if (items) {
          const filterDef = items.find((i: any) => i.title === filterTitle);
          return filterDef;
        }
      })
      .find(f => !!f);
  }

  private getNumericValueFilterFunc(filterName: string): ((product: Product) => boolean) | null {
    const numericValue = this.numericValueFilters.get(filterName);
    if (!numericValue) {
      return null;
    }

    const numericValueFilterFunc = (product: Product) => {
      const filterDef = this.getFilterDefinitionByTitle(filterName);

      const isMatched = !!filterDef?.isMatchFunc ? filterDef.isMatchFunc(product, numericValue) : true;

      return isMatched;
    };

    return numericValueFilterFunc;
  }

  private getRangeFilterFunc(filterName: string): ((product: Product) => boolean) | null {
    const rangeValues = this.rangeValueFilters.get(filterName);
    if (!rangeValues) {
      return null;
    }

    const rangeValueFilterFunc = (product: Product) => {
      const filterDef = this.getFilterDefinitionByTitle(filterName);

      return !!filterDef?.isMatchFunc ? filterDef.isMatchFunc(product, rangeValues[0], rangeValues[1]) : true;
    };

    return rangeValueFilterFunc;
  }

  private getAllNumericValueFilters() {
    return this.getAllNumericValueFilterKeys()
      .map(key => {
        const filterFunc = this.getNumericValueFilterFunc(key);
        if (filterFunc) {
          return filterFunc;
        }
      })
      .filter(r => !!r) as ((product: Product) => boolean)[];
  }

  private getAllRangeFilters() {
    return this.getAllRangeFilterKeys()
      .map(key => {
        const filterFunc = this.getRangeFilterFunc(key);
        if (filterFunc) {
          return filterFunc;
        }
      })
      .filter(r => !!r) as ((product: Product) => boolean)[];
  }

  private getAllNumericValueFilterKeys() {
    if (this.numericValueFilters?.size > 0) {
      return [...this.numericValueFilters.keys()];
    }

    return [];
  }

  private getAllRangeFilterKeys() {
    if (this.rangeValueFilters?.size > 0) {
      return [...this.rangeValueFilters.keys()];
    }

    return [];
  }

  private getGroupByFilterTitle(filterTitle: string) {
    return this.productFilterDefinitions.find(g => {
      const items = (g as any)['items'];
      if (items) {
        return items.some((i: any) => i.title === filterTitle);
      }
    });
  }

  private getGroupTitleByFilterTitle(filterTitle: string) {
    return this.getGroupByFilterTitle(filterTitle)?.title;
  }

  private getIncludeExcludeFilter(
    groupName: string | undefined,
    includeUnknown = false,
    matchedProducts: MatchedLenderProducts[] | null = null,
    matchedProductsFlat: Product[] | null = null,
    productsModelRequest: any,
  ): ((product: Product) => boolean) | null {
    if (!groupName) {
      return null;
    }

    if (!this.includeExcludeGroupsFilters.has(groupName)) {
      this.productsFilterHelper.resetSourcingFilters(matchedProductsFlat);
      return null;
    }

    const groupFilters = this.includeExcludeGroupsFilters.get(groupName);
    const includedFilterDefs = [...(groupFilters.includedFilterDefs?.values() || [])];
    const excludedFilterDefs = [...(groupFilters.excludedFilterDefs?.values() || [])];

    const hasIncluded = includedFilterDefs.length > 0;
    const hasExcluded = excludedFilterDefs.length > 0;

    if (!hasIncluded && !hasExcluded) {
      this.includeExcludeGroupsFilters.delete(groupName);
      this.productsFilterHelper.resetSourcingFilters(matchedProductsFlat);
      return null;
    }

    const includedFiltersNames = includedFilterDefs.map(f => f.title);

    const includeFilter = (product: Product) => {
      return includedFilterDefs.every(fd => {
        let isMatchedSubFilters = false;

        if (fd.hasSubFilters && fd.isMatchFuncFactory) {
          const checkedSubFilters = [...fd.checkedSubFilters];
          if (checkedSubFilters.length > 0) {
            const isMatch = checkedSubFilters.some(checkedFilterKey => fd.isMatchFuncFactory(checkedFilterKey)(product));
            isMatchedSubFilters = isMatch;
          }
        }

        const isUnknownMatched = !!fd.isUnknownFunc ? fd.isUnknownFunc(product, includedFiltersNames) : false;
        const isMatched = !!fd.isMatchFunc ? fd.isMatchFunc(product, matchedProducts, includedFiltersNames, productsModelRequest) : false;

        return includeUnknown ? isMatchedSubFilters || isMatched || isUnknownMatched : isMatchedSubFilters || isMatched;
      });
    };

    const excludeFilter = (product: Product) => {
      return excludedFilterDefs.every(fd => {
        let isMatchedSubFilters = false;

        if (fd.hasSubFilters && fd.isMatchFuncFactory) {
          const checkedSubFilters = [...fd.checkedSubFilters];
          if (checkedSubFilters.length === 0) {
            isMatchedSubFilters = true;
          } else {
            const isMatch = checkedSubFilters.every(checkedFilterKey => !fd.isMatchFuncFactory(checkedFilterKey)(product));
            isMatchedSubFilters = isMatch;
          }
        }

        let isMatched: boolean;
        if (fd.isExcludeFunc) {
          isMatched = fd.isExcludeFunc(product, includedFiltersNames);
        } else if (fd.isMatchFunc) {
          isMatched = !fd.isMatchFunc(product, matchedProducts, includedFiltersNames, productsModelRequest);
        } else {
          isMatched = false;
        }

        const isUnknownMatched = !!fd.isUnknownFunc ? fd.isUnknownFunc(product, includedFiltersNames) : false;
        return includeUnknown ? isMatchedSubFilters || isMatched || isUnknownMatched : isMatchedSubFilters || isMatched;
      });
    };

    const groupFilter = (product: Product) => {
      if (hasIncluded && hasExcluded) {
        return includeFilter(product) && excludeFilter(product);
      }
      if (hasIncluded) {
        return includeFilter(product);
      }
      return excludeFilter(product);
    };

    return groupFilter;
  }

  public toggleAllSubFilters(groupKey: string, filter: ProductFilter, selectAll: boolean, bulkUpdate = false) {
    if (!this.includeExcludeGroupsFilters.has(groupKey)) {
      return;
    }

    this.isPristine = false;
    const groupFilter = this.includeExcludeGroupsFilters.get(groupKey);
    const filterDefs = [
      ...(groupFilter.includedFilterDefs?.values() || []),
      ...(groupFilter.excludedFilterDefs?.values() || []),
    ];
    const filterDef = filterDefs.find(x => x.title === filter.title);
    if (!filterDef) {
      return;
    }

    filterDef.checkedSubFilters.clear();
    if (selectAll) {
      filter.subFilters?.items.forEach(i => filterDef.checkedSubFilters.add(i.title));
    }

    if (!bulkUpdate) {
      this.filterChanged.emit();
    }
  }

  public setSubFilter(groupKey: string, filterKey: string, subFilterKey: string, checked: boolean) {
    if (!this.includeExcludeGroupsFilters.has(groupKey)) {
      return;
    }

    this.isPristine = false;
    const groupFilter = this.includeExcludeGroupsFilters.get(groupKey);
    const filterDefs = [
      ...(groupFilter.includedFilterDefs?.values() || []),
      ...(groupFilter.excludedFilterDefs?.values() || []),
    ];
    const filterDef = filterDefs.find(x => x.title === filterKey);
    if (!filterDef) {
      return;
    }

    if (checked) {
      filterDef.checkedSubFilters.add(subFilterKey);
    } else {
      filterDef.checkedSubFilters.delete(subFilterKey);
    }

    this.filterChanged.emit();
  }

  public mapCriteriaToFilters(criteriaName: string | undefined): string | null {
    switch (criteriaName) {
      case 'Expat not in UK':
        return 'Expat not in UK';
      case 'Help to Buy':
        return 'Help to Buy';
      case '2nd Residential':
        return 'Second Residential';
      case 'Limited Company Buy to Let':
        return 'Limited Company Buy to Let';
      case 'Let to Buy':
        return 'Let to Buy';
      case 'Portfolio Landlord':
        return 'Portfolio Landlord';
      case 'Regulated Buy to Let':
        return 'Regulated BTL';
      default:
        return null;
    }
  }

  public setFilter(groupKey: string, filterKey: string, checked: boolean, bulkUpdate = false) {
    if (checked && !this.checkedFilters?.get(groupKey)?.has(filterKey)) {
      this.checkedFilters?.get(groupKey)?.add(filterKey);
    } else if (!checked && this.checkedFilters?.get(groupKey)?.has(filterKey)) {
      this.checkedFilters?.get(groupKey)?.delete(filterKey);
    }

    if (!bulkUpdate) {
      this.filterChanged.emit();
    }
  }

  public clearFilter(groupKey: string) {
    if (this.checkedFilters?.get(groupKey)) {
      this.checkedFilters?.get(groupKey)?.clear();
    }
  }

  public setFilters(filters: { groupKey: string; filterKey: string; checked: boolean }[]) {
    filters.forEach(filter => {
      this.setFilter(filter.groupKey, filter.filterKey, filter.checked, true);
    });

    this.filterChanged.emit();
  }

  /**
   * Creates a function based on the currently selected filters that will apply said filters to a product.
   * Will return `null` if there are no filters to be applied to the product.
   */
  public getFilterFunc(
    context: FilterOptionsContext<Product | BridgingProduct>,
    includeUnknown = true,
    matchedProducts: MatchedLenderProducts[] | null = null,
    matchedProductsFlat: Product[] | null = null,
    productsModelRequest: any,
  ): ((product: Product) => boolean) | null {
    const appliedFilters = this.getActiveFilters(context, true)
      .filter(({ items, group }) => items.length > 0 && !group.isIncludeExcludeFilter)
      .map(({ group, items }) => {
        const filterFuncs = items.map(filterLabel => {
          if ('itemFactory' in group) {
            return group.isMatchFuncFactory(filterLabel);
          }
          const filterDef = group.items.find(x => x.title === filterLabel);
          return includeUnknown && filterDef?.isUnknownFunc
            ? (p: Product) => !!filterDef.isUnknownFunc?.(p) || !!filterDef.isMatchFunc?.(p)
            : filterDef?.isMatchFunc;
        });
        return (product: Product) => filterFuncs[group.itemOperator === 'and' ? 'every' : 'some'](f => f?.(product));
      });

    this.getIncludeExcludeFilterContexts()
      .map(r => r.group)
      .forEach(g => {
        const includeExcludeFilter = this.getIncludeExcludeFilter(
          g?.title,
          includeUnknown,
          matchedProducts,
          matchedProductsFlat,
          productsModelRequest,
        );
        if (includeExcludeFilter) {
          appliedFilters.push(includeExcludeFilter);
        }
      });

    this.getAllNumericValueFilters().forEach(numericFilter => {
      appliedFilters.push(numericFilter);
    });

    this.getAllRangeFilters().forEach(rangeFilter => {
      appliedFilters.push(rangeFilter);
    });

    return appliedFilters.length > 0 ? (product: Product) => appliedFilters.every(f => f(product)) : null;
  }

  public clearGroupFilters(...groupNames: string[]) {
    if (!groupNames.length) {
      return;
    }
    const groupFilters = this.currentAppliedFilters?.filter(f => groupNames.includes(f.filterGroup));
    if (!groupFilters?.length) {
      return;
    }

    groupFilters.forEach(f => {
      // remove range filters
      this.rangeValueFilters.delete(f.filterName);

      // clear numeric filter
      this.numericValueFilters.delete(f.filterName);

      const bulkUpdate = true;
      this.setFilter(f.filterGroup, f.filterName, false, bulkUpdate);
    });
  }

  /**
   * Clears every filter the broker set, keeping the ones their firm has locked.
   *
   * A locked filter is not theirs to clear, and the panel already refuses to let them change it one at a time -
   * so clearing them all at once cannot be the way round that. Everything else goes, including their saved
   * preferences: "clear filters" means show me everything, not go back to my defaults.
   */
  public clearFilters(unsetProductFilters?: boolean, products?: Product[] | null) {
    const lockedGroups = this.filterPreferenceLocks.lockedGroups('product');
    const keptChecked = new Map(
      [...(this.checkedFilters ?? [])]
        .filter(([group]) => lockedGroups.has(group))
        .map(([group, titles]) => [group, new Set(titles)] as const),
    );
    const keptIncludeExclude = new Map(
      [...(this.includeExcludeGroupsFilters ?? [])].filter(([group]) => lockedGroups.has(group)),
    );
    const keptTitles = this.filterTitlesIn(lockedGroups);
    const keptNumeric = [...this.numericValueFilters].filter(([title]) => keptTitles.has(title));
    const keptRanges = [...this.rangeValueFilters].filter(([title]) => keptTitles.has(title));

    this.isPristine = true;
    this.availableFiltersCache.clear();
    this.setProductFilterDefinitions();

    this.includeExcludeGroupsFilters?.clear();
    this.clearNumericValueFilters();
    this.clearRangeFilters();
    this.checkedFilters?.forEach(grp => grp.clear());
    this.productsFilterHelper.resetSourcingFilters(products);

    keptChecked.forEach((titles, group) => this.checkedFilters?.set(group, titles));
    keptIncludeExclude.forEach((value, group) => this.includeExcludeGroupsFilters?.set(group, value));
    keptNumeric.forEach(([title, value]) => this.numericValueFilters.set(title, value));
    keptRanges.forEach(([title, value]) => this.rangeValueFilters.set(title, value));

    this.filterChanged.emit(unsetProductFilters);
  }

  /** Every filter title belonging to one of the given groups. Numeric and range values are keyed by title. */
  private filterTitlesIn(groups: ReadonlySet<string>): Set<string> {
    const titles = new Set<string>();

    for (const group of this.productFilterDefinitions) {
      if (!groups.has(group.title)) {
        continue;
      }

      for (const item of ((group as any)['items'] ?? []) as { title: string }[]) {
        titles.add(item.title);
      }
    }

    return titles;
  }

  public clearSourcingFilters() {
    this.productsFilterHelper.clearSourcingFilters();
  }

  private clearNumericValueFilters() {
    this.numericValueFilters?.clear();
  }

  public clearRangeFilters() {
    this.rangeValueFilters?.clear();
  }

  private isFilterAvailable(
    groupDef: ProductFilterGroupDefinition<Product | BridgingProduct>,
    filterDef: ProductFilterDefinition<Product | BridgingProduct>,
    ctx: FilterOptionsContext<Product | BridgingProduct>,
  ) {
    if (!groupDef.isIncludeExcludeFilter) {
      return true;
    }

    if (!filterDef.isMatchFunc) {
      return true;
    }

    const productsCount = ctx?.products?.length;
    const filterCacheKey = `${filterDef.title}_${productsCount}`;
    if (this.availableFiltersCache.has(filterCacheKey)) {
      return this.availableFiltersCache.get(filterCacheKey);
    }

    // Display filter when context has any products related to that filter
    // (has any products match to isMatchFunc OR isExcludeFunc filters)
    const isFilterAvailable = !!filterDef.isUnknownFunc
      ? ((ctx?.products?.some(p => (!!filterDef.isMatchFunc ? filterDef.isMatchFunc(p) : true)) ||
          (!!filterDef.isExcludeFunc && ctx?.products?.some(filterDef.isExcludeFunc))) ??
        ctx?.products?.some(filterDef.isUnknownFunc))
      : ctx?.products?.some(p => (!!filterDef.isMatchFunc ? filterDef.isMatchFunc(p) : true)) ||
        (!!filterDef.isExcludeFunc && ctx?.products?.some(filterDef.isExcludeFunc));

    this.availableFiltersCache.set(filterCacheKey, isFilterAvailable);

    return isFilterAvailable;
  }

  public excludeTailoredProductsByDefault(products: Product[] | BridgingProduct[] | null | undefined) {
    if (this.isPristine && !!products?.length) {
      this.isPristine = false;

      const excludedGroupTitle = 'Tailored products';
      const filterGroup = this.getFilterOptions({ products })?.find(g => g.title === excludedGroupTitle);
      const groupDef = this.productFilterDefinitions.find(g => g.title === excludedGroupTitle);

      if (groupDef) {
        const availableFilters = (groupDef as any)['items'].filter((filterDef: any) => {
          if (!!filterDef.visible) {
            return filterDef.visible(products) && this.isFilterAvailable(groupDef, filterDef, { products });
          }

          return this.isFilterAvailable(groupDef, filterDef, { products });
        });

        availableFilters.forEach((filterDef: any) => {
          // Set filters to excluded state so they show as "Exclude from results" by default
          this.setIncludeExcludeFilter(excludedGroupTitle, filterDef.title, false, true, true);
          if (filterDef.hasSubFilters && filterGroup) {
            const filter = filterGroup?.items.find(f => f.title === filterDef.title);
            if (filter) {
              this.toggleAllSubFilters(excludedGroupTitle, filter, true, true);
            }
          }
        });
        this.filterChanged.emit();
        this.isPristine = true;
      }
    }
  }

  public get activeFilterTitles() {
    const groupFilters = [...this.includeExcludeGroupsFilters.values()];
    const includedFilterDefs = groupFilters.flatMap(gf => [...(gf.includedFilterDefs?.values() || [])]);
    return includedFilterDefs.map(f => f.title);
  }

  public mapFiltersToYears(text: string): string {
    const match = /(\d{1,2}) years?/i.exec(text);
    return match ? `${+match[1] * 12}` : 'xx';
  }
}
